import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, UploadCloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import {
  adminListGalleryImages, adminAddGalleryImage, adminDeleteGalleryImage, adminUploadGalleryImages,
  adminListBeforeAfterPairs, apiErrorMessage,
} from '@/lib/api';
import { GALLERY_UPLOAD } from '@/constants/testIds';
import { BeforeAfterPairs } from './BeforeAfterPairs';
import { dialogClass, fieldClass } from './styles';

/**
 * Manage a gallery's photos: grid + delete, drag-and-drop upload to object
 * storage, paste-a-URL fallback, and before/after pairs.
 */
export const GalleryImagesDialog = ({ open, onOpenChange, slug }) => {
  const [images, setImages] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [addingImage, setAddingImage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setNewImageUrl('');
    adminListGalleryImages(slug).then((d) => { if (!cancelled) setImages(d); }).catch(() => { if (!cancelled) setImages([]); });
    adminListBeforeAfterPairs(slug).then((d) => { if (!cancelled) setPairs(d); }).catch(() => { if (!cancelled) setPairs([]); });
    return () => { cancelled = true; };
  }, [open, slug]);

  const handleAddImage = async (e) => {
    e.preventDefault();
    if (!newImageUrl.trim()) return;
    setAddingImage(true);
    try {
      const img = await adminAddGalleryImage(slug, { image_url: newImageUrl.trim(), order: images.length });
      setImages((prev) => [...prev, img]);
      setNewImageUrl('');
    } catch (err) {
      toast.error('Failed to add image.');
    } finally {
      setAddingImage(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await adminDeleteGalleryImage(slug, imageId);
      setImages((prev) => prev.filter((i) => i.id !== imageId));
      setPairs((prev) => prev.filter((p) => p.before_image_id !== imageId && p.after_image_id !== imageId));
    } catch (err) {
      toast.error('Failed to remove image.');
    }
  };

  const handleFilesSelected = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const created = await adminUploadGalleryImages(slug, files, setUploadProgress);
      setImages((prev) => [...prev, ...created]);
      toast.success(`Uploaded ${created.length} photo${created.length === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={dialogClass} data-testid="admin-gallery-images-dialog">
        <DialogHeader>
          <DialogTitle className="text-[hsl(var(--lxra-fg))]">Manage Images — {slug}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {images.map((img) => (
            <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden border border-[hsl(var(--lxra-border))]">
              <img src={img.image_url} alt={img.caption || ''} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => handleDeleteImage(img.id)}
                aria-label="Remove photo"
                data-testid={`admin-gallery-image-delete-${img.id}`}
                className="absolute top-1 right-1 h-6 w-6 flex items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
          {images.length === 0 && (
            <p className="col-span-3 text-sm text-[hsl(var(--lxra-muted))] py-6 text-center" data-testid="admin-gallery-images-empty">No images yet.</p>
          )}
        </div>

        <div
          data-testid={GALLERY_UPLOAD.dropzone}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFilesSelected(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          className={`mb-4 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-[hsl(var(--lxra-gold))] bg-[hsl(var(--lxra-gold))]/10' : 'border-[hsl(var(--lxra-border))] hover:border-[hsl(var(--lxra-gold))]/50'
          }`}
        >
          <UploadCloud className="h-6 w-6 text-[hsl(var(--lxra-gold))]" />
          <p className="text-sm text-[hsl(var(--lxra-fg))]">Drag & drop real photos here, or click to browse</p>
          <p className="text-xs text-[hsl(var(--lxra-muted))]">Uploads to secure storage — supports multiple files</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            data-testid={GALLERY_UPLOAD.fileInput}
            className="hidden"
            onChange={(e) => handleFilesSelected(e.target.files)}
          />
        </div>
        {uploading && (
          <div className="mb-4 space-y-1.5" data-testid={GALLERY_UPLOAD.progress}>
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-xs text-[hsl(var(--lxra-muted))] text-center">Uploading… {uploadProgress}%</p>
          </div>
        )}

        <form onSubmit={handleAddImage} className="flex gap-2">
          <Input
            placeholder="...or paste an Image URL"
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            className={fieldClass}
            data-testid="admin-gallery-image-url-input"
          />
          <Button type="submit" disabled={addingImage} data-testid="admin-gallery-image-url-add" className="h-10 px-4 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] flex-shrink-0">
            {addingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </form>

        {images.length >= 2 && <BeforeAfterPairs slug={slug} images={images} pairs={pairs} onPairsChange={setPairs} />}
      </DialogContent>
    </Dialog>
  );
};
